import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentAccount, useSuiClient } from "@mysten/dapp-kit";
import type { SuiJsonRpcClient } from "@mysten/sui/jsonRpc";
import { vaultBalances, readPolicyObject, type PolicyObject } from "@altheia-xyz/sui";
import { Sigil } from "@/components/Sigil";
import { ChainBadge } from "@/components/ChainBadge";
import { api, type AgentObject, type AuditEvent } from "@/lib/api";
import { suiExplorer, suiAgentIds, actionLabel, dedupAudit, auditActionLabel } from "@/lib/sui";
import { useAgentActions } from "@/lib/useAgentActions";
import { useTxAction } from "@/lib/useTxAction";
import { PREVIEW, previewAudit, previewBalances } from "@/lib/suiPreview";

/**
 * Sui AgentDetail body — the operator console for one agent.
 *
 * Layout: identity + owner controls (pause/revoke/recover) live in the HEADER
 * card; then live vault HOLDINGS, the on-chain POLICY (real caps + actions, with
 * an Edit modal for actions), then the per-action AUDIT trail. Every action runs
 * through useTxAction so the UI shows tx lifecycle and refetches on success.
 *
 * DESIGN.md (Persian + Orange): ghost ground, indigo structure, orange = live,
 * coral = denial. Mono data, serif-italic titles, full borders, reduced-motion safe.
 */

function short(id: string | null, head = 6, tail = 4): string {
  if (!id) return "—";
  return id.length <= head + tail + 1 ? id : `${id.slice(0, head)}…${id.slice(-tail)}`;
}

// Match by the type's module::TYPE tail — chain returns normalized, zero-padded
// type strings (e.g. 0000…02::sui::SUI), not the short 0x2::sui::SUI form.
function coinMeta(type: string): { symbol: string; decimals: number } {
  const t = type.toLowerCase();
  if (t.endsWith("::sui::sui")) return { symbol: "SUI", decimals: 9 };
  if (t.endsWith("::usdc::usdc")) return { symbol: "USDC", decimals: 6 };
  if (t.endsWith("::deep::deep")) return { symbol: "DEEP", decimals: 6 };
  const seg = type.split("::");
  return { symbol: seg.length >= 3 ? (seg[seg.length - 1] || "?") : short(type), decimals: 9 };
}
function fmtAmount(raw: string, decimals: number): string {
  const n = Number(raw) / 10 ** decimals;
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals >= 9 ? 4 : 2 });
}
function fmtExpiry(ms: number): string {
  if (!ms || ms <= 0) return "no expiry";
  const left = ms - Date.now();
  if (left <= 0) return "expired";
  const h = Math.floor(left / 3_600_000);
  const rel = h >= 24 ? `${Math.floor(h / 24)}d ${h % 24}h left` : `${h}h left`;
  return `${rel} · ${new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
}

// action id ↔ key for the edit modal (policy.move: swap=1, limit=2, cancel=3, transfer=0)
const EDIT_ACTIONS: { id: number; key: string; label: string; hint: string }[] = [
  { id: 1, key: "deepbook_swap", label: "Swap", hint: "market swap, value-guarded" },
  { id: 2, key: "deepbook_limit_order", label: "Limit order", hint: "place a resting order" },
  { id: 3, key: "deepbook_cancel", label: "Cancel order", hint: "cancel own orders" },
];

type Filter = "all" | "allowed" | "denied";
type Balance = { coinType: string; amount: string };

// Poll bail-outs: return the SAME reference when data is unchanged so React skips
// the re-render entirely (no flicker, no row re-animation). Only genuine changes
// produce a new array → new rows mount + fade in.
const sameEvents = (a: AuditEvent[], b: AuditEvent[]) => a.length === b.length && a.every((e, i) => e.id === b[i]?.id && e.decision === b[i]?.decision);
const sameBalances = (a: Balance[], b: Balance[]) => a.length === b.length && a.every((x, i) => x.coinType === b[i]?.coinType && x.amount === b[i]?.amount);

export function SuiAgentView({ agent, token }: { agent: AgentObject; token: string | null }) {
  const { vault, policy, cap } = suiAgentIds(agent);
  const client = useSuiClient() as unknown as SuiJsonRpcClient;
  const account = useCurrentAccount();
  const [filter, setFilter] = useState<Filter>("all");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [localRevoked, setLocalRevoked] = useState(false);
  const [editing, setEditing] = useState(false);
  const [chainPol, setChainPol] = useState<PolicyObject | null>(null);
  const auditLoadedRef = useRef(false);

  const refetch = () => setReloadKey((k) => k + 1);
  // No auto-poll on the detail page (avoids any re-render flicker). It loads on
  // open and refetches after your own actions; the Chronicle is the live feed.
  const actions = useAgentActions(agent.id, token, refetch);
  const ready = actions.ready || PREVIEW; // preview enables controls for layout QA
  // Live chain state wins; fall back to backend/optimistic before it loads.
  const revoked = (chainPol?.revoked ?? false) || agent.status === "revoked" || !!agent.revoked_at || localRevoked;
  const paused = chainPol?.paused ?? (agent.status === "paused");

  // Audit (chain poller + recorded denials/admin actions).
  useEffect(() => {
    if (PREVIEW) { setEvents(previewAudit(agent.id, filter)); setLoading(false); setErr(null); return; }
    if (!token) return;
    let cancelled = false;
    if (!auditLoadedRef.current) setLoading(true); // skeleton only first load, not on 3s polls
    setErr(null);
    api.getAgentAudit(agent.id, token, { filter, limit: 50 })
      .then((r) => { if (!cancelled) { setEvents((prev) => sameEvents(prev, r.events) ? prev : r.events); auditLoadedRef.current = true; } })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : "audit unavailable"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agent.id, token, filter, reloadKey]);

  // Live vault holdings (item 6) — read straight from chain.
  useEffect(() => {
    if (PREVIEW) { setBalances(previewBalances(vault ?? "")); return; }
    if (!vault) return;
    let cancelled = false;
    vaultBalances(client, vault).then((b) => { if (!cancelled) setBalances((prev) => sameBalances(prev, b) ? prev : b); }).catch(() => { /* keep last */ });
    return () => { cancelled = true; };
  }, [vault, client, reloadKey]);

  // Live policy straight from the on-chain Policy object (caps + actions + state).
  useEffect(() => {
    if (PREVIEW || !policy) return;
    let cancelled = false;
    readPolicyObject(client, policy).then((p) => { if (!cancelled) setChainPol((prev) => JSON.stringify(prev) === JSON.stringify(p) ? prev : p); }).catch(() => { /* keep last */ });
    return () => { cancelled = true; };
  }, [policy, client, reloadKey]);

  const evs = dedupAudit(events);
  const counts = {
    all: evs.length,
    allowed: evs.filter((e) => e.decision === "allowed").length,
    denied: evs.filter((e) => e.decision === "denied").length,
  };
  const pol = (agent.policy ?? {}) as Record<string, unknown>;
  const ID_TO_KEY: Record<number, string> = { 0: "transfer", 1: "deepbook_swap", 2: "deepbook_limit_order", 3: "deepbook_cancel" };
  type CapRow = { asset?: string; per_tx?: number | string; per_day?: number | string; spent_today?: number | string };
  // Chain-first: format the live policy caps (base units) by coin decimals; fall back to backend policy.
  const caps: CapRow[] | undefined = chainPol
    ? chainPol.caps.map((c) => {
        const m = coinMeta(c.coinType);
        const d = 10 ** m.decimals;
        return { asset: m.symbol, per_tx: Number(c.perTxCap) / d, per_day: Number(c.perDayCap) / d, spent_today: Number(c.spentToday) / d };
      })
    : ((pol.per_asset_caps ?? pol.caps) as CapRow[] | undefined);
  const allowedActions: string[] | undefined = chainPol
    ? chainPol.actions.map((id) => ID_TO_KEY[id]).filter(Boolean)
    : ((pol.allowed_actions ?? pol.actions) as string[] | undefined);
  const expiryMs = chainPol ? Number(chainPol.expiresAtMs) : (pol.expires_at ? Date.parse(String(pol.expires_at)) : 0);

  async function onPauseToggle() { paused ? await actions.unpause() : await actions.pause(); }
  async function onRevoke() { const r = await actions.revoke(); if (r.ok) setLocalRevoked(true); }
  async function onRecover() {
    if (!account) return;
    const held = balances.filter((b) => Number(b.amount) > 0);
    const types = held.map((b) => b.coinType);
    const main = held.find((b) => coinMeta(b.coinType).symbol === "SUI") ?? held[0];
    const m = main ? coinMeta(main.coinType) : null;
    const amt = main && m ? Number(main.amount) / 10 ** m.decimals : undefined;
    const r = await actions.recover(account.address, types, amt, m?.symbol); // drain only held; log the swept amount
    if (r.ok) setLocalRevoked(true);
  }

  const busy = actions.state.phase === "signing" || actions.state.phase === "pending";

  return (
    <div className="sv">
      <div className="sv__crumb">
        <Link to="/sui/dashboard">← Agents</Link><span className="sep">/</span><b>{agent.name}</b>
      </div>

      {(revoked || paused) && (
        <div className={`sv__banner ${revoked ? "is-revoked" : "is-paused"}`}>
          <span className="sv__banner-kick">{revoked ? "revoked" : "paused"}</span>
          <span>{revoked
            ? `Owner revoked this agent on-chain${agent.revoked_reason ? `, reason: ${agent.revoked_reason}` : ""}. Its next action aborts.`
            : "Agent is paused. It cannot transact until resumed."}</span>
          <Link className="sv__banner-link" to="/sui/audit">review chronicle →</Link>
        </div>
      )}

      {/* HEADER: identity + owner controls */}
      <div className="sv__card">
        <div className="sv__id">
          <div className="sv__seal"><Sigil seed={agent.name || agent.id} size={72} tone="bone" sweeping={!revoked} /></div>
          <div className="sv__idmain">
            <div className={`sv__status ${revoked ? "is-revoked" : paused ? "is-paused" : "is-active"}`}>
              <span className="dot" />{revoked ? "revoked" : paused ? "paused" : "active"}
            </div>
            <h1 className="sv__name">{agent.name}</h1>
            <div className="sv__meta"><ChainBadge chain="sui" /> · {agent.framework ?? "custom"} · {counts.all} events</div>
            <div className="sv__objs">
              <a href={vault ? suiExplorer.object(vault) : "#"} target="_blank" rel="noopener noreferrer"><b>Vault</b> {short(vault)} ↗</a>
              <a href={policy ? suiExplorer.object(policy) : "#"} target="_blank" rel="noopener noreferrer"><b>Policy</b> {short(policy)} ↗</a>
              <a href={cap ? suiExplorer.object(cap) : "#"} target="_blank" rel="noopener noreferrer"><b>AgentCap</b> {short(cap)} ↗</a>
            </div>
          </div>
        </div>
        <div className="sv__controls">
          <span className="sv__controls-lbl">operator · <b>on-chain enforced</b></span>
          <div className="sv__controls-btns">
            <button type="button" className="sv__btn sv__btn--ghost" disabled={!ready || revoked || busy} onClick={() => void onPauseToggle()}>{paused ? "Resume" : "Pause"}</button>
            <button type="button" className="sv__btn sv__btn--danger" disabled={!ready || revoked || busy} onClick={() => void onRevoke()}>{revoked ? "Revoked" : "Revoke"}</button>
          </div>
          <TxFeedback state={actions.state} ready={ready} />
        </div>
      </div>

      {/* HOLDINGS + POLICY (half / half) */}
      <section className="sv__panel">
        <div className="sv__split">
          <div className="sv__split-col">
            <div className="sv__ph"><h2>Holdings</h2><span className="sv__ph-link">live · in the vault now</span></div>
            {balances.filter((b) => Number(b.amount) > 0).length > 0 ? (
              <div className="sv__holds">
                {balances.filter((b) => Number(b.amount) > 0).map((b) => {
                  const m = coinMeta(b.coinType);
                  const capRow = caps?.find((c) => c.asset === m.symbol);
                  return (
                    <div className="sv__hold" key={b.coinType}>
                      <span className="sv__hold-mark">{m.symbol[0]}</span>
                      <span className="sv__hold-sym">{m.symbol}</span>
                      <span className="sv__hold-amt">{fmtAmount(b.amount, m.decimals)}</span>
                      <span className="sv__hold-cap">{capRow ? `cap ${capRow.per_day}/day` : "no cap"}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="sv__none">Vault is empty. Balances the agent holds or acquires appear here, read live from chain.</div>
            )}
            <button type="button" className="sv__btn sv__btn--ghost sv__withdraw" disabled={!ready || busy} onClick={() => void onRecover()} title="Revoke the agent and sweep every vault asset back to your wallet">Withdraw to wallet</button>
          </div>
          <div className="sv__split-col sv__split-col--r">
            <div className="sv__ph">
              <h2>Policy</h2>
              <div className="sv__ph-actions">
                <button type="button" className="sv__edit" disabled={!ready || revoked} onClick={() => setEditing(true)}>Edit</button>
                <a className="sv__ph-link" href={policy ? suiExplorer.object(policy) : "#"} target="_blank" rel="noopener noreferrer">on-chain ↗</a>
              </div>
            </div>
            <div className="sv__lbl">Per-asset caps <span className="sv__lbl-note">set at creation</span></div>
            {caps && caps.length > 0 ? (
              <table className="sv__tbl">
                <thead><tr><th>Asset</th><th className="r">Per tx</th><th className="r">Per day</th><th className="r">Spent</th></tr></thead>
                <tbody>{caps.map((c, i) => (
                  <tr key={i}><td>{c.asset ?? "—"}</td><td className="r">{c.per_tx ?? "—"}</td><td className="r">{c.per_day ?? "—"}</td><td className="r">{c.spent_today ?? 0}</td></tr>
                ))}</tbody>
              </table>
            ) : <div className="sv__none">No caps recorded. Open the on-chain object for live values.</div>}
            <div className="sv__lbl sv__lbl--mt">Allowed actions</div>
            {allowedActions && allowedActions.length > 0
              ? <div className="sv__chips">{allowedActions.map((a) => <span key={a} className="sv__chip">{actionLabel(a)}</span>)}</div>
              : <div className="sv__none">Default-deny. No actions allowed.</div>}
            <div className="sv__lbl sv__lbl--mt">Scope</div>
            {(chainPol?.allowedPackages ?? []).length > 0
              ? <div className="sv__chips">{chainPol!.allowedPackages.map((p) => <a key={p} className="sv__chip" href={suiExplorer.object(p)} target="_blank" rel="noopener noreferrer">DeepBook · {short(p)} ↗</a>)}</div>
              : <div className="sv__none">No package scope.</div>}
            <div className="sv__lbl sv__lbl--mt">Expires</div>
            <div className="sv__expiry">{fmtExpiry(expiryMs)}</div>
          </div>
        </div>
        <div className="sv__inscribed"><span className="mark">⊙ inscribed on sui</span> move-policy-object · value-conserved, witness-gated</div>
      </section>

      {/* AUDIT */}
      <section className="sv__panel">
        <div className="sv__ph">
          <h2>Audit log</h2>
          <div className="sv__tabs">
            {(["all", "allowed", "denied"] as Filter[]).map((f) => (
              <button key={f} className={filter === f ? "on" : ""} onClick={() => setFilter(f)}>{f} {counts[f] > 0 ? counts[f] : ""}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="sv__skeleton">{[0, 1, 2].map((i) => <div key={i} className="sv__skeleton-row" />)}</div>
        ) : err ? (
          <div className="sv__none">Audit poller has no events for this agent yet. They appear here once it acts on-chain.</div>
        ) : evs.length === 0 ? (
          <div className="sv__none">No {filter === "all" ? "" : filter} events yet.</div>
        ) : (
          <table className="sv__tbl sv__audit">
            <thead><tr><th>Time</th><th>Action</th><th className="r">Amount</th><th>Tx / reason</th><th className="r">Result</th></tr></thead>
            <tbody>{evs.map((e) => {
              const denied = e.decision === "denied";
              const d = e.action_descriptor ?? {};
              const when = e.block_time ?? e.created_at;
              return (
                <tr key={e.id} className={denied ? "is-deny" : ""}>
                  <td className="t">{new Date(when).toLocaleString()}</td>
                  <td>{auditActionLabel(e)}</td>
                  <td className="r">{d.amount != null ? `${d.amount} ${d.asset ?? ""}`.trim() : "—"}</td>
                  <td>{e.tx_signature
                    ? <a href={suiExplorer.tx(e.tx_signature)} target="_blank" rel="noopener noreferrer">{short(e.tx_signature, 8, 6)} ↗</a>
                    : <span className="sv__reason">{e.reason_detail ?? (e.reason_code ? actionLabel(e.reason_code) : "—")}</span>}</td>
                  <td className="r"><span className={`res ${denied ? "deny" : "ok"}`}>{denied ? "— denied" : "✓ allowed"}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </section>

      {editing && <EditPolicyModal agent={agent} allowed={allowedActions ?? []} actions={actions} onClose={() => setEditing(false)} />}

      <style>{`
        .sv { max-width: var(--page-max); margin: 0 auto; font-family: var(--font-body); color: var(--bone); }
        .sv__crumb { font: var(--type-mono-data); color: var(--slate); margin-bottom: var(--s-5); }
        .sv__crumb b { color: var(--bone); } .sv__crumb .sep { margin: 0 8px; color: var(--slate-3); }
        .sv__banner { display: flex; align-items: center; gap: var(--s-3); padding: 13px 18px; border: 1px solid var(--oxblood); background: var(--oxblood-tint); margin-bottom: var(--s-5); font-size: 13px; color: var(--bone-2); border-radius: var(--r-sm); }
        .sv__banner.is-paused { border-color: var(--accent-deep); background: var(--accent-tint); }
        .sv__banner-kick { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--oxblood); }
        .sv__banner.is-paused .sv__banner-kick { color: var(--accent-deep); }
        .sv__banner-link { margin-left: auto; font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--oxblood); }
        .sv__card { display: grid; grid-template-columns: 1fr auto; gap: var(--s-6); align-items: stretch; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); padding: var(--s-5) var(--s-6); margin-bottom: var(--s-5); }
        .sv__id { display: flex; gap: var(--s-5); }
        .sv__seal { width: 84px; height: 84px; border: 1px solid var(--line); border-radius: var(--r-sm); display: flex; align-items: center; justify-content: center; flex: none; background: var(--bg); }
        .sv__status { display: inline-flex; align-items: center; gap: 7px; font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; }
        .sv__status .dot { width: 8px; height: 8px; border-radius: 50%; }
        .sv__status.is-active { color: var(--ok); } .sv__status.is-active .dot { background: var(--accent); animation: ds-pulse var(--dur-pulse) ease-in-out infinite; }
        .sv__status.is-paused { color: var(--accent-deep); } .sv__status.is-paused .dot { background: var(--accent-deep); }
        .sv__status.is-revoked { color: var(--oxblood); } .sv__status.is-revoked .dot { background: var(--oxblood); }
        .sv__name { font: var(--type-display-page); font-style: italic; margin: 6px 0 8px; }
        .sv__meta { font: var(--type-mono-data); color: var(--slate); display: flex; align-items: center; gap: 6px; }
        .sv__objs { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--line-soft); font: var(--type-mono-address); }
        .sv__objs a { color: var(--slate); } .sv__objs a b { color: var(--bone); font-weight: 500; } .sv__objs a:hover { color: var(--accent-deep); }
        .sv__controls { display: flex; flex-direction: column; gap: var(--s-3); justify-content: center; min-width: 240px; border-left: 1px solid var(--line-soft); padding-left: var(--s-6); }
        .sv__controls-lbl { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.1em; color: var(--slate-3); } .sv__controls-lbl b { color: var(--accent-deep); }
        .sv__controls-btns { display: flex; gap: var(--s-2); flex-wrap: wrap; }
        .sv__btn { font: var(--type-label); letter-spacing: 0.03em; text-transform: uppercase; padding: 8px 14px; border-radius: var(--r-sm); background: var(--indigo); color: var(--text-on-ink); transition: background 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
        .sv__btn:hover:not(:disabled) { background: var(--indigo-deep); }
        .sv__btn:active:not(:disabled) { transform: scale(0.97); }
        .sv__btn:disabled { background: var(--line); color: var(--slate-3); cursor: not-allowed; }
        .sv__btn--ghost { background: transparent; border: 1px solid var(--line); color: var(--bone); }
        .sv__btn--ghost:hover:not(:disabled) { background: var(--bg-elev); border-color: var(--line-stamp); }
        .sv__btn--danger { background: var(--oxblood); }
        .sv__btn--danger:hover:not(:disabled) { background: var(--oxblood-bright, var(--oxblood)); }
        .sv__panel { background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); padding: var(--s-5) var(--s-6); margin-bottom: var(--s-5); }
        .sv__ph { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--s-5); }
        .sv__ph h2 { font: var(--type-display-section); font-style: italic; }
        .sv__ph-actions { display: flex; align-items: center; gap: var(--s-4); }
        .sv__ph-link, .sv__ph-link:visited { font: var(--type-mono-address); color: var(--slate); }
        .sv__ph-link:hover { color: var(--accent-deep); }
        .sv__edit { font: var(--type-label); text-transform: uppercase; letter-spacing: 0.04em; color: var(--indigo); border: 1px solid var(--line); border-radius: var(--r-sm); padding: 6px 14px; transition: border-color 150ms ease, background 150ms ease, transform 120ms cubic-bezier(0.23,1,0.32,1); }
        .sv__edit:hover:not(:disabled) { border-color: var(--indigo); background: var(--indigo-tint); }
        .sv__edit:active:not(:disabled) { transform: scale(0.97); }
        .sv__edit:disabled { color: var(--slate-3); cursor: not-allowed; }
        .sv__split { display: grid; grid-template-columns: 1fr 1fr; }
        .sv__split-col { display: flex; flex-direction: column; padding-right: var(--s-6); }
        .sv__split-col--r { padding-right: 0; padding-left: var(--s-6); border-left: 1px solid var(--line-soft); }
        .sv__holds { display: flex; flex-direction: column; gap: var(--s-2); align-items: flex-start; }
        .sv__withdraw { align-self: flex-start; margin-top: auto; }
        .sv__lbl--mt { margin-top: var(--s-5); }
        .sv__expiry { font: var(--type-mono-data); color: var(--bone-2); }
        .sv__hold { display: flex; align-items: center; gap: 10px; padding: 10px 16px 10px 10px; border: 1px solid var(--line); border-radius: var(--r-pill); background: var(--bg); }
        .sv__hold-mark { width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; background: var(--indigo-tint); color: var(--indigo); font: var(--type-mono-data); font-weight: 700; font-size: 11px; }
        .sv__hold-sym { font: var(--type-mono-data); font-weight: 600; color: var(--bone); }
        .sv__hold-amt { font-family: var(--font-mono); font-variant-numeric: tabular-nums; font-size: 16px; color: var(--bone); }
        .sv__hold-cap { font: var(--type-mono-address); color: var(--slate-2); }
        .sv__cols { display: grid; grid-template-columns: 1.4fr 1fr; gap: var(--s-7); }
        .sv__lbl { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); margin-bottom: var(--s-3); display: flex; gap: 8px; align-items: baseline; }
        .sv__lbl-note { font: var(--type-mono-address); text-transform: none; letter-spacing: 0; color: var(--slate-3); }
        .sv__tbl { width: 100%; border-collapse: collapse; }
        .sv__tbl th { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.08em; color: var(--slate-3); text-align: left; font-weight: 400; padding: 0 0 8px; }
        .sv__tbl th.r, .sv__tbl td.r { text-align: right; }
        .sv__tbl td { font: var(--type-mono-data); padding: 9px 0; border-top: 1px solid var(--line-soft); }
        .sv__tbl th:not(:last-child), .sv__tbl td:not(:last-child) { padding-right: var(--s-6); }
        .sv__chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .sv__chip { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; padding: 4px 10px; border: 1px solid var(--line); color: var(--bone-2); border-radius: var(--r-sm); }
        .sv__none { font: var(--type-body-small); color: var(--slate-2); line-height: 1.5; max-width: 52ch; }
        .sv__inscribed { display: flex; align-items: center; gap: 10px; margin-top: var(--s-5); padding-top: var(--s-4); border-top: 1px solid var(--line-soft); font: var(--type-mono-address); color: var(--slate-2); }
        .sv__inscribed .mark { color: var(--accent-deep); text-transform: uppercase; letter-spacing: 0.08em; }
        .sv__tabs { display: flex; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; }
        .sv__tabs button { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; padding: 7px 13px; border: 0; border-right: 1px solid var(--line); background: var(--bg); color: var(--slate); }
        .sv__tabs button:last-child { border-right: 0; } .sv__tabs button.on { background: var(--bone); color: var(--text-on-ink); }
        .sv__audit td.t { color: var(--slate-2); } .sv__audit a { color: var(--slate); } .sv__audit a:hover { color: var(--accent-deep); }
        .sv__audit tr.is-deny td { background: var(--oxblood-tint); }
        .sv__audit tbody tr { animation: sv-row-in 300ms cubic-bezier(0.23,1,0.32,1); }
        @keyframes sv-row-in { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .sv__audit tbody tr { animation: none; } }
        .sv__reason { color: var(--oxblood); }
        .res { font: var(--type-mono-address); text-transform: uppercase; letter-spacing: 0.06em; } .res.ok { color: var(--ok); } .res.deny { color: var(--oxblood); }
        .sv__skeleton { display: flex; flex-direction: column; gap: 8px; }
        .sv__skeleton-row { height: 28px; background: linear-gradient(90deg, var(--line-soft), var(--bg), var(--line-soft)); background-size: 200% 100%; animation: sv-shimmer 1.2s ease-in-out infinite; }
        @keyframes sv-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @media (max-width: 860px) { .sv__card { grid-template-columns: 1fr; } .sv__controls { border-left: 0; padding-left: 0; border-top: 1px solid var(--line-soft); padding-top: var(--s-4); min-width: 0; } .sv__split { grid-template-columns: 1fr; } .sv__split-col { padding-right: 0; } .sv__split-col--r { padding-left: 0; border-left: 0; border-top: 1px solid var(--line-soft); padding-top: var(--s-5); margin-top: var(--s-5); } }
        @media (prefers-reduced-motion: reduce) { .sv__skeleton-row, .sv__status .dot { animation: none !important; } }
      `}</style>
    </div>
  );
}

/** Inline tx lifecycle feedback for the header controls (item 7). */
function TxFeedback({ state, ready }: { state: ReturnType<typeof useTxAction>["state"]; ready: boolean }) {
  if (!ready) return <span className="sv__txfb is-muted">control keys not on this device — view-only</span>;
  const { phase } = state;
  if (phase === "idle") return null;
  const map: Record<string, { cls: string; text: string }> = {
    signing: { cls: "is-wait", text: "approve in your wallet…" },
    pending: { cls: "is-wait", text: "submitting on-chain…" },
    success: { cls: "is-ok", text: `✓ done${state.digest ? "" : ""}` },
    failed: { cls: "is-fail", text: `✗ ${state.error ?? "failed"}` },
    rejected: { cls: "is-fail", text: "rejected in wallet" },
  };
  const m = map[phase];
  return (
    <span className={`sv__txfb ${m.cls}`}>
      {m.text}
      {phase === "success" && state.digest && <> · <a href={suiExplorer.tx(state.digest)} target="_blank" rel="noopener noreferrer">{short(state.digest, 6, 4)} ↗</a></>}
      <style>{`
        .sv__txfb { font: var(--type-mono-address); letter-spacing: 0.02em; }
        .sv__txfb.is-muted { color: var(--slate-3); }
        .sv__txfb.is-wait { color: var(--accent-deep); }
        .sv__txfb.is-ok { color: var(--ok); } .sv__txfb.is-ok a { color: var(--ok); }
        .sv__txfb.is-fail { color: var(--oxblood); }
      `}</style>
    </span>
  );
}

/**
 * Edit policy — fixed-size modal, internal scroll, two tabs (visual controls +
 * JSON), both prefilled from the live policy. v1 edits the ALLOWED ACTIONS
 * (admin_set_actions); caps are creation-only on the contract and shown
 * read-only on the page. Apply → on-chain tx → records a policy_updated row.
 */
function EditPolicyModal({ agent, allowed, actions, onClose }: {
  agent: AgentObject;
  allowed: string[];
  actions: ReturnType<typeof useAgentActions>;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"controls" | "json">("controls");
  const initial = useMemo(() => new Set(EDIT_ACTIONS.filter((a) => allowed.includes(a.key)).map((a) => a.id)), [allowed]);
  const [sel, setSel] = useState<Set<number>>(initial);
  const [jsonText, setJsonText] = useState(() => JSON.stringify({ allowed_actions: EDIT_ACTIONS.filter((a) => allowed.includes(a.key)).map((a) => a.key) }, null, 2));
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const phase = actions.state.phase;
  const busy = phase === "signing" || phase === "pending";

  function toggle(id: number) {
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function syncJsonToSel() {
    setJsonText(JSON.stringify({ allowed_actions: EDIT_ACTIONS.filter((a) => sel.has(a.id)).map((a) => a.key) }, null, 2));
  }

  async function apply() {
    let ids: number[];
    if (tab === "json") {
      try {
        const o = JSON.parse(jsonText) as { allowed_actions?: string[] };
        ids = EDIT_ACTIONS.filter((a) => (o.allowed_actions ?? []).includes(a.key)).map((a) => a.id);
        setJsonErr(null);
      } catch { setJsonErr("invalid JSON"); return; }
    } else {
      ids = [...sel];
    }
    const r = await actions.setActions(ids);
    if (r.ok) onClose();
  }

  return (
    <div className="em__scrim" onClick={onClose}>
      <div className="em" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="em__head">
          <h2>Edit policy — {agent.name}</h2>
          <button type="button" className="em__x" onClick={onClose} aria-label="close">×</button>
        </div>
        <div className="em__tabs">
          <button className={tab === "controls" ? "on" : ""} onClick={() => setTab("controls")}>Controls</button>
          <button className={tab === "json" ? "on" : ""} onClick={() => { syncJsonToSel(); setTab("json"); }}>JSON</button>
        </div>
        <div className="em__body">
          {tab === "controls" ? (
            <>
              <div className="em__lbl">Allowed actions</div>
              <div className="em__checks">
                {EDIT_ACTIONS.map((a) => {
                  const on = sel.has(a.id);
                  return (
                    <label key={a.id} className={`em__check ${on ? "is-on" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(a.id)} />
                      <span className="em__check-box" aria-hidden><svg viewBox="0 0 12 12" width="11" height="11"><path d="M2.5 6.2l2.2 2.3L9.6 3.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                      <span className="em__check-main"><span className="em__check-lbl">{a.label}</span><span className="em__check-hint">{a.hint}</span></span>
                    </label>
                  );
                })}
              </div>
              <p className="em__note">Per-asset caps are set at creation and can't change on a live policy. Re-mint to change a budget.</p>
            </>
          ) : (
            <>
              <textarea className="em__json" spellCheck={false} value={jsonText} onChange={(e) => setJsonText(e.target.value)} />
              {jsonErr && <div className="em__err">{jsonErr}</div>}
              <p className="em__note">Editing <code>allowed_actions</code> here updates the on-chain policy. Caps and scope are read-only.</p>
            </>
          )}
        </div>
        <div className="em__foot">
          <TxFeedback state={actions.state} ready={actions.ready || PREVIEW} />
          <div className="em__foot-btns">
            <button type="button" className="sv__btn sv__btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="sv__btn" onClick={() => void apply()} disabled={busy}>{busy ? "Applying…" : "Apply on-chain →"}</button>
          </div>
        </div>
        <style>{`
          .em__scrim { position: fixed; inset: 0; z-index: 60; display: grid; place-items: center; padding: var(--s-5); background: color-mix(in srgb, var(--indigo) 28%, transparent); animation: em-fade 160ms ease-out; }
          @keyframes em-fade { from { opacity: 0; } to { opacity: 1; } }
          .em { width: 440px; max-width: 100%; height: 520px; max-height: 90vh; display: flex; flex-direction: column; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--r-md); box-shadow: 0 24px 60px -16px color-mix(in srgb, var(--indigo) 36%, transparent); transform-origin: center; animation: em-in 200ms cubic-bezier(0.23,1,0.32,1); }
          @keyframes em-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: none; } }
          .em__head { display: flex; align-items: center; justify-content: space-between; padding: var(--s-5) var(--s-5) var(--s-4); border-bottom: 1px solid var(--line-soft); }
          .em__head h2 { font: var(--type-display-section); font-style: italic; font-size: 20px; }
          .em__x { font-size: 22px; line-height: 1; color: var(--slate-2); padding: 4px 8px; border-radius: var(--r-sm); transition: color 120ms ease, transform 120ms ease; } .em__x:hover { color: var(--bone); } .em__x:active { transform: scale(0.9); }
          .em__tabs { display: flex; gap: 2px; padding: var(--s-3) var(--s-5) 0; }
          .em__tabs button { font: var(--type-label); text-transform: uppercase; letter-spacing: 0.04em; color: var(--slate); padding: 8px 16px; border-bottom: 2px solid transparent; transition: color 120ms ease, border-color 120ms ease; }
          .em__tabs button.on { color: var(--bone); border-color: var(--accent); }
          .em__body { flex: 1; min-height: 0; overflow-y: auto; padding: var(--s-5); display: flex; flex-direction: column; gap: var(--s-3); }
          .em__lbl { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-3); }
          .em__checks { display: flex; flex-direction: column; gap: var(--s-2); }
          .em__check { display: flex; align-items: center; gap: var(--s-3); padding: 11px 13px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--bg); cursor: pointer; transition: border-color 150ms ease, background 150ms ease; }
          .em__check.is-on { border-color: var(--indigo); background: var(--indigo-tint); }
          .em__check input { position: absolute; opacity: 0; width: 0; height: 0; }
          .em__check-box { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border: 1px solid var(--line-stamp); border-radius: 4px; background: var(--bg-elev); color: transparent; flex: none; transition: background 150ms ease, border-color 150ms ease; }
          .em__check.is-on .em__check-box { background: var(--indigo); border-color: var(--indigo); color: var(--text-on-ink); }
          .em__check-main { display: flex; flex-direction: column; gap: 1px; }
          .em__check-lbl { font: var(--type-body); font-weight: 500; }
          .em__check-hint { font: var(--type-body-small); color: var(--slate-2); }
          .em__json { width: 100%; flex: 1; min-height: 220px; resize: none; font: var(--type-mono-data); line-height: 1.6; color: var(--bone); background: var(--bg); border: 1px solid var(--line); border-radius: var(--r-sm); padding: var(--s-4); }
          .em__err { font: var(--type-mono-address); color: var(--oxblood); }
          .em__note { font: var(--type-body-small); color: var(--slate-2); line-height: 1.5; margin: 0; }
          .em__foot { display: flex; align-items: center; justify-content: space-between; gap: var(--s-4); padding: var(--s-4) var(--s-5); border-top: 1px solid var(--line-soft); }
          .em__foot-btns { display: flex; gap: var(--s-2); margin-left: auto; }
          @media (prefers-reduced-motion: reduce) { .em, .em__scrim { animation: none; } }
        `}</style>
      </div>
    </div>
  );
}
